// src/components/crm/IncomingCallToast.jsx
//
// Screen-pop for inbound Zadarma calls. Mounted exactly once, in
// BaseLayout (the app shell every authenticated page renders through), so
// it is live no matter which page the agent is on. Subscribes to
// `ws/crm/telephony/` — the receiving end lives in `crm/consumers.py`,
// which forwards `call.incoming` / `call.answered` / `call.ended` verbatim
// from `crm/services/call_webhook.py`. The message shape is not owned here
// and must not be reinvented: `{type, pbx_call_id, number, match_kind,
// match_id}` for `call.incoming`; `{type, pbx_call_id}` for the other two.
//
// WebSocket URL convention copied from `useAssistantWebSocket.js` (the only
// other hand-rolled `new WebSocket(...)` in this app that needs auth) —
// `REACT_APP_WS_URL`, falling back to deriving host/protocol from
// `REACT_APP_API_URL`, falling back to the page's own host with :3000
// swapped for :8000 for local dev. Do not invent a different convention.
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FaPhoneAlt, FaTimes, FaUserPlus } from 'react-icons/fa';
import { AuthContext } from '../../context/AuthContext';
import crmService from '../../services/crmService';
import { customerService } from '../../services/customerService';
import api from '../../services/api';
import logger from '../../utils/logger';

const MAX_RETRIES = 10;

function buildWsUrl(token) {
    let wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    let wsHost = process.env.REACT_APP_WS_URL;
    if (wsHost && wsHost.includes('://')) {
        try {
            const parsed = new URL(wsHost);
            wsHost = parsed.host;
            wsProtocol = ['wss:', 'https:'].includes(parsed.protocol) ? 'wss:' : 'ws:';
        } catch (e) {
            wsHost = null;
        }
    }
    if (!wsHost) {
        const apiUrl = process.env.REACT_APP_API_URL;
        if (apiUrl) {
            try {
                const parsed = new URL(apiUrl);
                wsHost = parsed.host;
                wsProtocol = parsed.protocol === 'https:' ? 'wss:' : 'ws:';
            } catch (e) { /* fall through to window host */ }
        }
    }
    if (!wsHost) {
        wsHost = window.location.host.replace(':3000', ':8000');
    }
    return `${wsProtocol}//${wsHost}/ws/crm/telephony/?token=${token}`;
}

// Resolves a {match_kind, match_id} pair from the webhook payload into a
// display label + a link into the existing record. `contact` has no
// standalone detail page in this app, so a contact match resolves through
// to whichever of its lead/customer parent it belongs to.
async function resolveMatch(matchKind, matchId) {
    try {
        if (matchKind === 'lead') {
            const lead = await crmService.getLead(matchId);
            return { label: lead.shop_name || `Lead #${matchId}`, link: `/crm/leads/${matchId}` };
        }
        if (matchKind === 'customer') {
            const result = await customerService.getCustomerById(matchId);
            if (result.success) {
                return { label: result.data.name || `Customer #${matchId}`, link: `/customers/${matchId}` };
            }
        }
        if (matchKind === 'contact') {
            const { data: contact } = await api.get(`/crm/api/contacts/${matchId}/`);
            const name = [contact.first_name, contact.last_name].filter(Boolean).join(' ') || `Contact #${matchId}`;
            if (contact.lead) {
                return { label: name, link: `/crm/leads/${contact.lead}` };
            }
            if (contact.customer) {
                return { label: name, link: `/customers/${contact.customer}` };
            }
            return { label: name, link: null };
        }
    } catch (err) {
        logger.warn('[IncomingCallToast] failed to resolve match', err);
    }
    return null;
}

const IncomingCallToast = () => {
    const { isAuthenticated } = useContext(AuthContext);
    const navigate = useNavigate();
    const [activeCall, setActiveCall] = useState(null);

    const wsRef = useRef(null);
    const reconnectTimerRef = useRef(null);
    const retryCountRef = useRef(0);
    const isMountedRef = useRef(true);
    // Guards against a resolveMatch() for an already-dismissed/replaced call
    // landing late and reviving a stale toast.
    const activeCallIdRef = useRef(null);

    const clearReconnectTimer = () => {
        if (reconnectTimerRef.current) {
            clearTimeout(reconnectTimerRef.current);
            reconnectTimerRef.current = null;
        }
    };

    const dismiss = useCallback((pbxCallId) => {
        if (!pbxCallId || activeCallIdRef.current === pbxCallId) {
            activeCallIdRef.current = null;
            setActiveCall(null);
        }
    }, []);

    const handleIncoming = useCallback(async (msg) => {
        activeCallIdRef.current = msg.pbx_call_id;
        setActiveCall({
            pbxCallId: msg.pbx_call_id,
            number: msg.number,
            matchKind: msg.match_kind,
            matchId: msg.match_id,
            label: null,
            link: null,
        });

        if (msg.match_kind && msg.match_id) {
            const resolved = await resolveMatch(msg.match_kind, msg.match_id);
            // The call may have already ended/been superseded by the time
            // the lookup resolves — don't resurrect a dismissed toast.
            if (resolved && isMountedRef.current && activeCallIdRef.current === msg.pbx_call_id) {
                setActiveCall((prev) => (
                    prev && prev.pbxCallId === msg.pbx_call_id ? { ...prev, ...resolved } : prev
                ));
            }
        }
    }, []);

    useEffect(() => {
        isMountedRef.current = true;

        if (!isAuthenticated) {
            // Logout (or not-yet-logged-in): make sure nothing is left
            // running from a previous session.
            clearReconnectTimer();
            if (wsRef.current) {
                wsRef.current.close();
                wsRef.current = null;
            }
            setActiveCall(null);
            return undefined;
        }

        const connect = () => {
            const token = localStorage.getItem('access_token');
            if (!token) return;

            const socket = new WebSocket(buildWsUrl(token));
            wsRef.current = socket;

            socket.onopen = () => {
                retryCountRef.current = 0;
            };

            socket.onmessage = (event) => {
                let msg;
                try {
                    msg = JSON.parse(event.data);
                } catch (err) {
                    return;
                }
                if (msg.type === 'call.incoming') {
                    handleIncoming(msg);
                } else if (msg.type === 'call.answered' || msg.type === 'call.ended') {
                    dismiss(msg.pbx_call_id);
                }
            };

            socket.onclose = () => {
                if (!isMountedRef.current || !isAuthenticated) return;
                if (retryCountRef.current < MAX_RETRIES) {
                    const delay = Math.min(3000 * 2 ** retryCountRef.current, 30000);
                    retryCountRef.current += 1;
                    reconnectTimerRef.current = setTimeout(connect, delay);
                }
            };

            socket.onerror = () => {
                // onclose fires right after; reconnect handled there.
            };
        };

        connect();

        return () => {
            isMountedRef.current = false;
            clearReconnectTimer();
            if (wsRef.current) {
                wsRef.current.close();
                wsRef.current = null;
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAuthenticated]);

    if (!activeCall) return null;

    const handleCreateLead = () => {
        dismiss(activeCall.pbxCallId);
        const phone = activeCall.number ? `&phone=${encodeURIComponent(activeCall.number)}` : '';
        navigate(`/crm/leads?new=1${phone}`);
    };

    return (
        <div className="fixed top-4 right-4 z-[9999] w-80 bg-white border border-blue-200 rounded-lg shadow-lg overflow-hidden">
            <div className="flex items-start gap-3 p-4">
                <div className="flex-shrink-0 mt-0.5 text-green-600">
                    <FaPhoneAlt className="w-5 h-5 animate-bounce" />
                </div>
                <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Incoming call</p>
                    {activeCall.link ? (
                        <>
                            <p className="text-sm font-medium text-gray-900 truncate">{activeCall.label}</p>
                            <p className="text-xs text-gray-500">{activeCall.number}</p>
                            <Link
                                to={activeCall.link}
                                onClick={() => dismiss(activeCall.pbxCallId)}
                                className="inline-block mt-2 text-xs font-medium text-blue-600 hover:underline"
                            >
                                View record
                            </Link>
                        </>
                    ) : (
                        <>
                            <p className="text-sm font-medium text-gray-900 truncate">
                                {activeCall.label || activeCall.number || 'Unknown number'}
                            </p>
                            <button
                                type="button"
                                onClick={handleCreateLead}
                                className="inline-flex items-center gap-1 mt-2 text-xs font-medium text-blue-600 hover:underline"
                            >
                                <FaUserPlus className="w-3 h-3" /> Create lead
                            </button>
                        </>
                    )}
                </div>
                <button
                    type="button"
                    onClick={() => dismiss(activeCall.pbxCallId)}
                    className="flex-shrink-0 text-gray-400 hover:text-gray-600"
                    aria-label="Dismiss"
                >
                    <FaTimes className="w-4 h-4" />
                </button>
            </div>
        </div>
    );
};

export default IncomingCallToast;
